import { useEffect, useRef, useState, type ReactNode } from 'react'

export type ToolbarMenuItem = {
  key: string
  label: string
  onSelect: () => void
  disabled?: boolean
}

type ToolbarMenuProps = {
  label: ReactNode
  title?: string
  items: ToolbarMenuItem[]
  disabled?: boolean
}

const PANEL_MIN_WIDTH = 150

/**
 * Dropdown simples da toolbar. O painel usa position: fixed para não ser
 * cortado pelo overflow-x da toolbar, permanecendo no DOM do editor para
 * herdar as variáveis de tema (light/dark).
 */
export function ToolbarMenu({
  label,
  title,
  items,
  disabled = false,
}: ToolbarMenuProps) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const toggle = () => {
    if (open) {
      setOpen(false)
      return
    }
    const rect = buttonRef.current?.getBoundingClientRect()
    if (!rect) return
    setPos({
      top: rect.bottom + 4,
      left: Math.max(
        8,
        Math.min(rect.left, window.innerWidth - PANEL_MIN_WIDTH - 8),
      ),
    })
    setOpen(true)
  }

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        buttonRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return
      }
      setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    const close = () => setOpen(false)
    window.addEventListener('mousedown', onPointerDown)
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('resize', close)
    window.addEventListener('scroll', close, true)
    return () => {
      window.removeEventListener('mousedown', onPointerDown)
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', close)
      window.removeEventListener('scroll', close, true)
    }
  }, [open])

  return (
    <span className="toolbar-menu">
      <button
        ref={buttonRef}
        type="button"
        title={title}
        disabled={disabled}
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {label} <span aria-hidden="true">▾</span>
      </button>
      {open && pos && (
        <div
          ref={panelRef}
          className="toolbar-menu-panel"
          role="menu"
          style={{ top: pos.top, left: pos.left }}
        >
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              className="toolbar-menu-item"
              disabled={item.disabled}
              onClick={() => {
                setOpen(false)
                item.onSelect()
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </span>
  )
}
